'use strict';

(() => {
  const validId = (value) => Number.isInteger(Number(value)) && Number(value) > 0;

  function sanitize(item) {
    if (!item || !validId(item.variantId)) return null;
    const normalized = {
      variantId: Number(item.variantId),
      quantity: Math.max(1, Math.trunc(Number(item.quantity) || 1)),
      handle: String(item.handle || ''),
      title: String(item.title || ''),
      variantTitle: String(item.variantTitle || ''),
      image: String(item.image || ''),
      url: String(item.url || ''),
      price: Math.max(0, Math.trunc(Number(item.price) || 0)),
    };
    if (typeof item.hasOnlyDefaultVariant === 'boolean') normalized.hasOnlyDefaultVariant = item.hasOnlyDefaultVariant;
    return normalized;
  }

  function normalize(items) {
    if (!Array.isArray(items)) return [];
    const byVariant = new Map();
    for (const raw of items) {
      const item = sanitize(raw);
      if (item) byVariant.set(item.variantId, item);
    }
    return [...byVariant.values()];
  }

  function save(items, rawItem) {
    const item = sanitize(rawItem);
    if (!item) return normalize(items);
    return [...normalize(items).filter((candidate) => candidate.variantId !== item.variantId), item];
  }

  const remove = (items, variantId) => normalize(items).filter((item) => item.variantId !== Number(variantId));
  const find = (items, variantId) => normalize(items).find((item) => item.variantId === Number(variantId)) || null;

  function lineIdentity(item) {
    if (!item || !validId(item.variant_id ?? item.variantId)) return null;
    const properties = (Array.isArray(item.properties) ? [...item.properties] : Object.entries(item.properties || {})).sort(([a], [b]) => String(a).localeCompare(String(b)));
    const sellingPlan = item.selling_plan_allocation?.selling_plan?.id ?? item.sellingPlanId ?? null;
    return JSON.stringify([Number(item.variant_id ?? item.variantId), properties, sellingPlan == null ? null : String(sellingPlan)]);
  }

  function resolveLine(items, key, hint) {
    if (!Array.isArray(items)) return null;
    const identity = lineIdentity(hint);
    if (!identity) return null;
    const exact = items.find((item) => item.key === key);
    if (exact) return lineIdentity(exact) === identity ? exact : null;
    const matches = items.filter((item) => lineIdentity(item) === identity);
    return matches.length === 1 ? matches[0] : null;
  }

  function lineQuantities(items) {
    const quantities = new Map();
    for (const item of items || []) {
      const identity = lineIdentity(item);
      if (!identity) return null;
      quantities.set(identity, (quantities.get(identity) || 0) + Number(item.quantity || 0));
    }
    return quantities;
  }

  function quantityChangePlan(items, sourceKey, quantity, hint) {
    const source = resolveLine(items, sourceKey, hint);
    const before = lineQuantities(items);
    if (!source || !before) return null;
    const identity = lineIdentity(source);
    const nextQuantity = Math.max(1, Math.trunc(Number(quantity) || 1));
    const expected = new Map(before);
    expected.set(identity, before.get(identity) - Number(source.quantity) + nextQuantity);
    return { id: source.key, quantity: nextQuantity, identity, before: [...before], expected: [...expected] };
  }

  function quantityChangeVerified(cart, plan, original = false) {
    if (!plan || !Array.isArray(cart?.items)) return false;
    const actual = lineQuantities(cart.items);
    const expected = new Map(original ? plan.before : plan.expected);
    return Boolean(actual) && actual.size === expected.size && [...expected].every(([identity, quantity]) => actual.get(identity) === quantity);
  }

  function variantUpdatePlan(items, sourceKey, targetVariantId, quantity) {
    const lines = Array.isArray(items) ? items : [];
    const source = lines.find((item) => item.key === sourceKey);
    if (!source || !validId(targetVariantId)) return null;
    const nextQuantity = Math.max(1, Math.trunc(Number(quantity) || 1));
    const targetId = Number(targetVariantId);
    const targetBefore = lines
      .filter((item) => Number(item.variant_id) === targetId && item.key !== sourceKey)
      .reduce((sum, item) => sum + Number(item.quantity || 0), 0);

    if (Number(source.variant_id) === targetId) {
      return {
        updates: { [sourceKey]: nextQuantity },
        sourceKey,
        sourceVariantId: Number(source.variant_id),
        sourceQuantity: Number(source.quantity),
        targetVariantId: targetId,
        targetBefore: Math.max(0, targetBefore),
        expectedTarget: nextQuantity,
        sameVariant: true,
      };
    }

    const existingTarget = lines.find((item) => item.key !== sourceKey && Number(item.variant_id) === targetId);
    const updates = { [sourceKey]: 0 };
    updates[existingTarget?.key || String(targetId)] = Number(existingTarget?.quantity || 0) + nextQuantity;
    return {
      updates,
      sourceKey,
      sourceVariantId: Number(source.variant_id),
      sourceQuantity: Number(source.quantity),
      targetVariantId: targetId,
      targetBefore: Math.max(0, targetBefore),
      expectedTarget: Math.max(0, targetBefore) + nextQuantity,
      targetKey: existingTarget?.key || null,
      sameVariant: false,
    };
  }

  function variantUpdateVerified(cart, plan) {
    if (!plan || !Array.isArray(cart?.items)) return false;
    const targetQuantity = cart.items
      .filter((item) => Number(item.variant_id) === plan.targetVariantId)
      .reduce((sum, item) => sum + Number(item.quantity || 0), 0);
    if (plan.sameVariant) {
      const source = cart.items.find((item) => item.key === plan.sourceKey);
      return Boolean(source) && Number(source.quantity) === plan.expectedTarget;
    }
    return !cart.items.some((item) => item.key === plan.sourceKey) && targetQuantity === plan.expectedTarget;
  }

  function variantRollbackPlan(beforeItems, afterItems, plan) {
    if (!plan || !Array.isArray(beforeItems) || !Array.isArray(afterItems)) return null;
    const updates = {};
    if (plan.sameVariant) {
      const afterSource = afterItems.find((item) => item.key === plan.sourceKey);
      updates[afterSource?.key || String(plan.sourceVariantId)] = plan.sourceQuantity;
      return { updates };
    }
    const beforeTarget = beforeItems.find((item) => item.key !== plan.sourceKey && Number(item.variant_id) === plan.targetVariantId);
    const afterTarget = afterItems.find((item) => Number(item.variant_id) === plan.targetVariantId);
    if (beforeTarget) updates[beforeTarget.key] = Number(beforeTarget.quantity);
    else if (afterTarget) updates[afterTarget.key] = 0;
    else updates[String(plan.targetVariantId)] = 0;

    const afterSource = afterItems.find((item) => item.key === plan.sourceKey);
    if (afterSource) updates[afterSource.key] = plan.sourceQuantity;
    else updates[String(plan.sourceVariantId)] = plan.sourceQuantity;
    return { updates };
  }

  function variantOriginalVerified(cart, beforeItems, plan) {
    if (!plan || !Array.isArray(cart?.items) || !Array.isArray(beforeItems)) return false;
    const ids = new Set([plan.sourceVariantId, plan.targetVariantId]);
    for (const variantId of ids) {
      const beforeQuantity = beforeItems
        .filter((item) => Number(item.variant_id) === variantId)
        .reduce((sum, item) => sum + Number(item.quantity || 0), 0);
      const currentQuantity = cart.items
        .filter((item) => Number(item.variant_id) === variantId)
        .reduce((sum, item) => sum + Number(item.quantity || 0), 0);
      if (beforeQuantity !== currentQuantity) return false;
    }
    return true;
  }

  const core = {
    sanitize,
    normalize,
    save,
    remove,
    find,
    lineIdentity,
    resolveLine,
    quantityChangePlan,
    quantityChangeVerified,
    variantUpdatePlan,
    variantUpdateVerified,
    variantRollbackPlan,
    variantOriginalVerified,
  };
  if (typeof module !== 'undefined') module.exports = core;
  if (typeof window !== 'undefined') window.OluvieReferenceCartCore = core;
})();
